import React, { PureComponent } from 'react';
import { connect } from 'react-redux';
import { default as Dz } from 'react-dropzone';
import uuidv4 from 'uuid/v4';
import update from 'immutability-helper';
import FileIcon, { defaultStyles } from 'react-file-icon';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import Request from 'superagent';

// Material UI
import LinearProgress from '@material-ui/core/LinearProgress';
import Divider from '@material-ui/core/Divider';

// Local
import { Upload, Trash } from 'components/Icons';
import { fileExtension, formatBytes } from 'helpers/utils';
import { BASE_URL, THUMBNAIL_SIZE } from 'consts';

export class Dropzone extends PureComponent {
  state = {
    files: [
      /**
       * File object plus...
       * {
       *   key,
       *   name,
       *   size,
       *   ext,
       *   progress,
       * },
       */
    ],
    completed: 0,
  };

  deleteFileFromQueue = file => {
    const index = this.state.files.map(el => el.key).indexOf(file.key);
    if (index >= 0) {
      this.setState(
        prevState => ({
          files: update(prevState.files, { $splice: [[index, 1]] }),
        }),
        this.props.onFileDeleted(file)
      );
    }
  };

  uploadFiles = () => {
    this.state.files.forEach(file => {
      Request.post(`${BASE_URL}/files/upload`)
        .attach('file', file)
        .set('Authorization', this.props.auth.token)
        .timeout(3600000) // 1 hour
        .on('progress', event => {
          this.updateFileProgress(file.key, Math.round(event.percent || 100));
        })
        .end((err, res) => {
          if (!err && res.statusCode === 201) {
            if (this.props.createThumb && file.type.startsWith('image')) {
              this.createThumb(file)
                .then(thumb => {
                  this.deleteFileFromQueue(file);
                  this.props.onFinishUpload(res.body, thumb);
                })
                .catch(err => this.props.onHandleError(err));
            } else {
              this.deleteFileFromQueue(file);
              this.props.onFinishUpload(res.body);
            }
          } else {
            this.props.onHandleError(err);
          }
        });
    });
  };

  updateFileProgress = (fileKey, progress) => {
    const copy = this.state.files.slice();
    const index = copy.map(el => el.key).indexOf(fileKey);
    copy[index].progress = progress;
    this.setState({ files: copy });
  };

  createThumb = file => {
    return new Promise(function(resolve, reject) {
      const reader = new FileReader();

      reader.onload = event => {
        const img = new Image();
        img.src = event.target.result;

        img.onload = () => {
          const canvas = document.createElement('canvas');
          canvas.width = THUMBNAIL_SIZE;
          canvas.height = THUMBNAIL_SIZE;

          const scale = canvas.width / Math.max(img.width, img.height);
          const w = img.width * scale;
          const h = img.height * scale;

          const ctx = canvas.getContext('2d');
          ctx.fillStyle = 'white';
          ctx.fillRect(0, 0, canvas.width, canvas.height);
          ctx.drawImage(
            img,
            (canvas.width - w) / 2,
            (canvas.height - h) / 2,
            w,
            h
          );
          const data = canvas.toDataURL('image/jpeg', 0.8);

          resolve(data);
        };
      };

      reader.onerror = () => {
        reject();
      };

      reader.onabort = () => {
        reject();
      };

      reader.readAsDataURL(file);
    });
  };

  renderRow = file => {
    return (
      <div key={file.key}>
        <div className="text-sm pl-2 pr-2 pt-1 pb-1 flex justify-between">
          <div>
            <FileIcon
              extension={file.ext}
              size={18}
              labelUppercase
              {...defaultStyles[file.ext]}
            />
          </div>
          <div className="flex flex-grow justify-between pl-1">
            <div>
              <span className="text-sm">{file.name}</span>
              <span className="text-xs text-grey">{` (${formatBytes(
                file.size
              )})`}</span>
            </div>
            <div className="ml-4 cursor-pointer">
              <Trash
                color="#4EA2FF"
                style={{ width: 18, height: 18 }}
                onClick={() => this.deleteFileFromQueue(file)}
              />
            </div>
          </div>
        </div>
        {file.progress > 0 && (
          <LinearProgress
            className="ml-2 mr-2 mb-1"
            variant="determinate"
            value={file.progress}
          />
        )}
        <Divider className="m-2" />
      </div>
    );
  };

  progress = () => {
    const { completed } = this.state;
    if (completed === 100) {
      this.setState({ completed: 0 });
    } else {
      const diff = Math.random() * 10;
      this.setState({ completed: Math.min(completed + diff, 100) });
    }
  };

  onDrop = accepted => {
    const newFiles = accepted
      // Add extra props
      .map(file => {
        file.ext = fileExtension(file.name);
        file.key = uuidv4();
        file.progress = 0;
        return file;
      })
      // Do not accepts duplicate
      .reduce((acc, cur) => {
        const found = this.state.files.find(el => el.name === cur.name);
        if (!found) acc.push(cur);
        return acc;
      }, []);
    if (newFiles.length) {
      this.setState(
        update(this.state, { files: { $push: newFiles } }),
        this.props.onFilesAdded(newFiles)
      );
    }
  };

  render() {
    const { dropText, accept, orientation, minHeight } = this.props;

    return (
      <div
        style={{ minHeight: minHeight }}
        className={classnames('flex', {
          'flex-col': orientation === 'vertical',
        })}
      >
        <Dz
          className="flex flex-grow bg-blue-lightest border border-dashed border-blue p-4 rounded items-center justify-center cursor-pointer"
          accept={accept}
          onDrop={this.onDrop}
        >
          <Upload color="#4EA2FF" />
          <span className="ml-2 mb-1 text-blue text-sm">{dropText}</span>
        </Dz>
        {this.state.files.length > 0 && (
          <div
            className={classnames(
              'flex flex-col bg-white text-sm text-blue',
              {
                'mt-4': orientation === 'vertical',
              },
              { 'pl-4': orientation === 'horizontal' }
            )}
          >
            {this.state.files.map(file => {
              return this.renderRow(file);
            })}
          </div>
        )}
      </div>
    );
  }
}

Dropzone.defaultProps = {
  minHeight: '10em',
  dropText: 'Drop your files here or click to select',
  parallel: true,
  orientation: 'vertical',
};

Dropzone.propTypes = {
  minHeight: PropTypes.string,
  dropText: PropTypes.string,
  parallel: PropTypes.bool,
  accept: PropTypes.string.isRequired,
  orientation: PropTypes.oneOf(['vertical', 'horizontal']).isRequired,
  onFilesAdded: PropTypes.func,
  onFileDeleted: PropTypes.func,
  onFinishUpload: PropTypes.func,
  onHandleError: PropTypes.func,
};

const mapStateToProps = state => ({
  auth: state.auth,
});

export default connect(
  mapStateToProps,
  null,
  null,
  { withRef: true }
)(Dropzone);
